import type { ComponentProps } from "react";
import { LoaderCircle } from "lucide-react";
import { cn } from "@/lib/utils";

// One spinner and one skeleton for the whole app. Which to reach for is
// decided by what is unavailable, not by how it looks: the whole route, one
// card or section, the action a button just started, or a single value inside
// a layout that is otherwise already drawn.
//
// `label` is a prop rather than a t() call because scripts/audit-ui-strings.mjs
// treats any literal under src/components as untranslated copy. Callers pass
// t("common.loading") or something more specific.

export function Spinner({
  label,
  className,
  ...props
}: ComponentProps<typeof LoaderCircle> & { label?: string }) {
  return (
    <>
      <LoaderCircle
        className={cn("animate-spin", className)}
        aria-hidden="true"
        {...props}
      />
      {label ? <span className="sr-only">{label}</span> : null}
    </>
  );
}

/**
 * A value the layout has already made room for. Preferred over an em dash or a
 * zero: both read as an answer, and a stat that says 0 while it loads is worse
 * than one that says nothing yet.
 */
export function Skeleton({ className, ...props }: ComponentProps<"span">) {
  return (
    <span
      className={cn("block animate-pulse rounded-md bg-neutral", className)}
      aria-hidden="true"
      {...props}
    />
  );
}

/** Route-level: the page has nothing to show yet. */
export function PageLoader({ label }: { label: string }) {
  return (
    <div
      role="status"
      className="flex min-h-64 w-full flex-col items-center justify-center gap-3"
    >
      <Spinner className="size-6 text-primary" />
      <span className="text-sm text-neutral-foreground">{label}</span>
    </div>
  );
}

/** Card or section level: the surrounding page chrome is already usable. */
export function SectionLoader({
  label,
  className,
}: {
  label: string;
  className?: string;
}) {
  return (
    <div
      role="status"
      className={cn(
        "flex min-h-24 w-full items-center justify-center gap-2",
        className,
      )}
    >
      <Spinner className="size-4 text-neutral-foreground" />
      <span className="text-xs text-neutral-foreground">{label}</span>
    </div>
  );
}

/**
 * In-button: the action is in flight. Rendered by Button's `loading` prop
 * rather than by callers, so every submit in the app behaves the same way —
 * label stays put, width does not jump, and the control stops accepting input.
 */
export function ButtonLoader({ label }: { label?: string }) {
  return <Spinner className="size-4" label={label} />;
}
